import { default as React } from 'react';
import { jsx } from 'react/jsx-runtime';

/**
 * The registry of {@link ComponentInterceptor} instances
 */
const interceptorRegistry: ComponentInterceptor[] = [];

/**
 * Component interceptors are used by {@link injectInterceptorShim}. Any time {@link React.createElement} is called,
 * any registered interceptors will be evaluated.
 */
export interface ComponentInterceptor {
  /**
   * A function that determines if this interceptor can intercept the call to
   * {@Link react/jsx-runtime.jsx} with the given `type`, `props` and `key`.
   * @param type
   * @param props
   * @param key
   */
  canIntercept(type: React.ElementType, props: any, key: React.Key): boolean;

  /**
   * Performs any actions on the given `props` or `original` element. Should return a valid {@link Element}.
   * @param type
   * @param props
   * @param key
   */
  performIntercept(
    type: React.ElementType,
    props: any,
    key: React.Key,
  ): {
    element?: JSX.Element;
    jsxFnProps?: {
      props: any;
      type: React.ElementType;
      key: React.Key;
    };
  };
}

/**
 * Registers a component interceptor to replace a component that is part of
 * a library which cannot be changed otherwise.
 *
 * @param interceptor The interceptor
 */
export const registerComponentInterceptor = (
  interceptor: ComponentInterceptor,
): void => {
  interceptorRegistry.push(interceptor);
};

/**
 * Injects the interceptor code to enable {@link ComponentInterceptor} implementations
 * to take effect.
 *
 * Note: This will only work if the running code **preserves function names**.
 */
export const injectInterceptorShim = () => {
  const originalJsx = jsx;
  // @ts-ignore
  // eslint-disable-next-line func-names
  jsx = function (type, props, key) {
    const jsxInterceptor = interceptorRegistry.find(interceptor =>
      interceptor.canIntercept(type, props, key),
    );
    if (jsxInterceptor) {
      const result = jsxInterceptor.performIntercept(type, props, key);
      if (result?.element && React.isValidElement(result.element)) {
        return result.element;
      }
      if (result?.jsxFnProps) {
        const {
          props: jsxProps,
          type: jsxType,
          key: jsxKey,
        } = result.jsxFnProps;
        return originalJsx(jsxType, jsxProps, jsxKey);
      }
    }
    return originalJsx(type, props, key);
  };
};
